import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../api";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Check, X, Download, AlertTriangle, Calendar } from "lucide-react";

const LIENS_LABEL = {
  conjoint: "Conjoint(e)", enfant: "Enfant", parent: "Parent",
  frere_soeur: "Frère/Sœur", petit_enfant: "Petit-enfant",
  neveu_niece: "Neveu/Nièce", autre: "Autre",
};

const STATUS_LABEL = {
  pending: { label: "À valider", color: "#caa86a", bg: "rgba(202,168,106,0.12)" },
  approved: { label: "Approuvée", color: "#2f7d5b", bg: "rgba(47,125,91,0.12)" },
  rejected: { label: "Refusée", color: "#b53a3a", bg: "rgba(181,58,58,0.1)" },
};

export default function AdminHeritier() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [selected, setSelected] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectMotif, setRejectMotif] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/heritier-requests");
    setItems(r.data.requests || []);
    setPending(r.data.pending || 0);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Confirmer l'approbation de cette demande ? Un lien d'accès sera envoyé au demandeur.")) return;
    try {
      await api.post(`/admin/heritier-requests/${id}/approve`);
      await load();
      setSelected(null);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const reject = async () => {
    if (!rejectMotif.trim()) return;
    try {
      await api.post(`/admin/heritier-requests/${rejecting.id}/reject`, { motif: rejectMotif });
      await load();
      setRejecting(null);
      setRejectMotif("");
      setSelected(null);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const downloadActe = async (id) => {
    try {
      const r = await api.get(`/admin/heritier-requests/${id}/acte-deces`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-10" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="max-w-6xl mx-auto space-y-6">
        <Link to="/admin" className="flex items-center gap-2" style={{ color: "var(--auprea-blue)", fontSize: 14 }} data-testid="back-admin">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="flex items-center gap-3">
          <Heart size={26} style={{ color: "var(--auprea-gold)" }} />
          <div>
            <h1 style={{ fontSize: 32, margin: 0 }}>Demandes d'accès héritier</h1>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)", margin: 0 }}>
              {pending} demande{pending > 1 ? "s" : ""} en attente sur {items.length} au total.
            </p>
          </div>
        </div>

        {error && (
          <div className="auprea-card" style={{ color: "#b53a3a", background: "#fcf3f3" }}>{error}</div>
        )}

        <div className="auprea-card">
          {items.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(35,38,46,0.5)" }}>
              Aucune demande pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="heritier-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(21,41,79,0.1)", color: "rgba(35,38,46,0.6)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    <th className="text-left py-3 pr-4">Statut</th>
                    <th className="text-left py-3 pr-4">Défunt</th>
                    <th className="text-left py-3 pr-4">Demandeur</th>
                    <th className="text-left py-3 pr-4">Lien</th>
                    <th className="text-left py-3 pr-4">Date demande</th>
                    <th className="text-right py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const st = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
                    return (
                      <tr key={r.id} style={{ borderBottom: "1px solid rgba(21,41,79,0.05)" }} data-testid={`row-${r.id}`}>
                        <td className="py-3 pr-4">
                          <span style={{ padding: "3px 10px", fontSize: 11, fontWeight: 600, color: st.color, background: st.bg, borderRadius: 999 }}>
                            {st.label}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <div style={{ fontWeight: 600, color: "var(--auprea-navy)" }}>{r.defunt_nom_complet}</div>
                          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{r.defunt_email}</div>
                          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.5)" }}>
                            <Calendar size={11} style={{ display: "inline", marginRight: 4 }} />
                            {new Date(r.defunt_date_deces).toLocaleDateString("fr-FR")}
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <div>{r.demandeur_prenom} {r.demandeur_nom}</div>
                          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{r.demandeur_email}</div>
                          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{r.demandeur_telephone}</div>
                        </td>
                        <td className="py-3 pr-4">{LIENS_LABEL[r.lien_avec_defunt] || r.lien_avec_defunt}</td>
                        <td className="py-3 pr-4 text-xs" style={{ color: "rgba(35,38,46,0.6)" }}>
                          {new Date(r.created_at).toLocaleString("fr-FR")}
                        </td>
                        <td className="py-3 text-right">
                          <button onClick={() => setSelected(r)} className="text-sm" style={{ color: "var(--auprea-blue)", textDecoration: "underline" }} data-testid={`details-${r.id}`}>
                            Détails
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal détails */}
        {selected && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }} onClick={() => setSelected(null)} data-testid="heritier-modal">
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", maxWidth: 640, width: "100%", padding: "32px 36px", borderRadius: 8, maxHeight: "90vh", overflowY: "auto" }}>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>Demande d'accès héritier</h2>
              <p style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", marginBottom: 22 }}>Reçue le {new Date(selected.created_at).toLocaleString("fr-FR")}</p>

              <div className="space-y-4">
                <Box title="Défunt" rows={[
                  ["Nom complet", selected.defunt_nom_complet],
                  ["Email AUPREA", selected.defunt_email],
                  ["Date du décès", new Date(selected.defunt_date_deces).toLocaleDateString("fr-FR")],
                ]} />
                <Box title="Demandeur" rows={[
                  ["Nom", `${selected.demandeur_prenom} ${selected.demandeur_nom}`],
                  ["Email", selected.demandeur_email],
                  ["Téléphone", selected.demandeur_telephone],
                  ["Lien avec le défunt", LIENS_LABEL[selected.lien_avec_defunt]],
                ]} />
                {selected.message && (
                  <div style={{ background: "rgba(243,239,230,0.6)", padding: 16, borderRadius: 4 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(35,38,46,0.6)", fontWeight: 600, marginBottom: 6 }}>Message</div>
                    <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{selected.message}</div>
                  </div>
                )}
                <button onClick={() => downloadActe(selected.id)} className="flex items-center gap-2" style={{ color: "var(--auprea-blue)", fontSize: 14, textDecoration: "underline" }} data-testid="download-acte">
                  <Download size={15} /> Télécharger l'acte de décès
                </button>

                {selected.status === "rejected" && selected.rejection_reason && (
                  <div style={{ background: "#fcf3f3", border: "1px solid #f0c5c5", padding: 14, borderRadius: 4, fontSize: 13, color: "#7a2828" }}>
                    <strong>Motif du refus :</strong> {selected.rejection_reason}
                  </div>
                )}
              </div>

              {selected.status === "pending" ? (
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button onClick={() => approve(selected.id)} className="flex items-center justify-center gap-2 py-3" style={{ background: "var(--auprea-green)", color: "#fff", fontWeight: 600, borderRadius: 4 }} data-testid="approve-btn">
                    <Check size={16} /> Approuver et envoyer l'accès
                  </button>
                  <button onClick={() => setRejecting(selected)} className="flex items-center justify-center gap-2 py-3" style={{ background: "#fff", color: "#b53a3a", border: "1px solid #b53a3a", borderRadius: 4, fontWeight: 600 }} data-testid="reject-btn">
                    <X size={16} /> Refuser
                  </button>
                </div>
              ) : (
                <button onClick={() => setSelected(null)} className="auprea-btn w-full mt-6">Fermer</button>
              )}
            </div>
          </div>
        )}

        {/* Modal refus */}
        {rejecting && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.6)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setRejecting(null)}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", maxWidth: 480, width: "100%", padding: "30px 32px", borderRadius: 8 }}>
              <h2 style={{ fontSize: 20, marginBottom: 4 }}>Motif du refus</h2>
              <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 18 }}>
                Sera communiqué au demandeur par email.
              </p>
              <textarea className="auprea-input" rows={4} value={rejectMotif} onChange={(e) => setRejectMotif(e.target.value)} placeholder="Ex : justificatif insuffisant, identité non vérifiée…" data-testid="reject-motif" />
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => { setRejecting(null); setRejectMotif(""); }} style={{ color: "rgba(35,38,46,0.7)", padding: "8px 16px" }} data-testid="reject-cancel">Annuler</button>
                <button onClick={reject} disabled={!rejectMotif.trim()} className="auprea-btn" style={{ background: "#b53a3a" }} data-testid="reject-confirm">Refuser la demande</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Box({ title, rows }) {
  return (
    <div style={{ border: "1px solid rgba(21,41,79,0.1)", padding: 16, borderRadius: 4 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--auprea-gold)", fontWeight: 600, marginBottom: 10 }}>— {title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: "4px 12px 4px 0", color: "rgba(35,38,46,0.6)", verticalAlign: "top", width: 140 }}>{k}</td>
              <td style={{ padding: "4px 0", color: "var(--auprea-ink)" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
